<template>
  <div class="billing-invoices">
    <EntraPageHeader
      title="Billing Invoices"
      :showTenantInfo="false"
      :showBreadcrumb="false"
      :showSearch="false"
      :showSavedViews="false"
      :showExport="false"
      :showRefresh="true"
      @refresh="loadData"
    />

    <div class="page-body">
      <div class="filters-bar">
        <div class="filter-group">
          <label class="filter-label">Region:</label>
          <select v-model="selectedRegion" class="filter-select" @change="onRegionChange">
            <option value="">Select Region</option>
            <option v-for="region in regions" :key="region.region_code" :value="region.region_code">
              {{ region.region_code }}
            </option>
          </select>
        </div>
        <div class="filter-group">
          <label class="filter-label">Type:</label>
          <select v-model="filters.invoiceType" class="filter-select" @change="applyFilters">
            <option value="">All Types</option>
            <option value="reconciliation">Reconciliation</option>
            <option value="azure-usage">Azure Usage</option>
          </select>
        </div>
      </div>

      <div v-if="error" class="error-message">
        {{ error }}
      </div>

      <div v-else-if="!selectedRegion" class="empty-wrapper">
        <EmptyState title="Select a region" description="Choose a billing region to view invoices." />
      </div>

      <div v-else-if="tableState === 'loading'" class="loading-state">
        <span>Loading invoices...</span>
      </div>

      <div v-else-if="tableState === 'empty'" class="empty-wrapper">
        <EmptyState title="No invoices found" description="No invoices match the current filters for this region." />
      </div>

      <div v-else class="table-wrapper">
        <table class="data-table">
          <thead>
            <tr>
              <th>Invoice ID</th>
              <th>Type</th>
              <th>Period Start</th>
              <th>Period End</th>
              <th>Total Charges</th>
              <th>Currency</th>
              <th>Status</th>
              <th>Collected</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="invoice in invoices" :key="invoice.invoice_id + invoice.region_code">
              <td class="invoice-id-cell">{{ invoice.invoice_id }}</td>
              <td>
                <span class="type-chip" :class="'type-' + invoice.invoice_type">{{ invoice.invoice_type }}</span>
              </td>
              <td>{{ formatDate(invoice.billing_period_start, 'short') }}</td>
              <td>{{ formatDate(invoice.billing_period_end, 'short') }}</td>
              <td>
                <div class="spend-cell">
                  <span class="spend-value">{{ formatCurrency(invoice.total_charges, invoice.currency_code) }}</span>
                  <div v-if="invoice.total_charges" class="spend-bar-container">
                    <div class="spend-bar" :style="{ width: spendBarWidth(invoice.total_charges) + '%' }"></div>
                  </div>
                </div>
              </td>
              <td>{{ invoice.currency_code || '--' }}</td>
              <td>
                <span class="status-chip" :class="'status-' + (invoice.status || 'unknown')">{{ invoice.status || '--' }}</span>
              </td>
              <td>{{ formatRelativeTime(invoice.collected_at) }}</td>
              <td>
                <button class="export-btn" :disabled="exportingId === invoice.invoice_id" @click="exportInvoice(invoice.invoice_id)">{{ exportingId === invoice.invoice_id ? '...' : 'CSV' }}</button>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="pagination" v-if="totalPages > 1">
          <button class="page-btn" :disabled="page <= 1" @click="goToPage(page - 1)">Previous</button>
          <span class="page-info">Page {{ page }} of {{ totalPages }}</span>
          <button class="page-btn" :disabled="page >= totalPages" @click="goToPage(page + 1)">Next</button>
        </div>
      </div>

      <div v-if="exportError" class="error-message" style="margin-top: 12px;">
        {{ exportError }}
      </div>
    </div>
  </div>
</template>

<script>
import EntraPageHeader from '@/components/entra/common/EntraPageHeader.vue';
import EmptyState from '@/components/entra/common/EmptyState.vue';
import { getBillingRegions, getBillingInvoices, downloadBillingInvoiceCsv } from '@/services/billing-api.js';
import { formatCurrency } from '@/services/entra-utils.js';
import { formatDate } from '@/services/date-utils.js';

export default {
  name: 'BillingInvoices',
  components: { EntraPageHeader, EmptyState },
  data: function() {
    return {
      regions: [],
      selectedRegion: '',
      invoices: [],
      tableState: 'loading',
      filters: { invoiceType: '' },
      page: 1,
      totalPages: 1,
      pageSize: 50,
      error: '',
      loadToken: 0,
      exportingId: '',
      exportError: ''
    };
  },
  methods: {
    formatCurrency: function(amount, currency) {
      return formatCurrency(amount, currency);
    },
    formatDate: function(dateStr, mode) {
      return formatDate(dateStr, { mode: mode || 'short' });
    },
    formatRelativeTime: function(isoString) {
      if (!isoString) return '--';
      return formatDate(isoString, { mode: 'relative' });
    },
    spendBarWidth: function(value) {
      if (!this.invoices || this.invoices.length === 0) return 0;
      var max = 0;
      for (var i = 0; i < this.invoices.length; i++) {
        var v = Number(this.invoices[i].total_charges || 0);
        if (v > max) max = v;
      }
      return max > 0 ? Math.round((Number(value || 0) / max) * 100) : 0;
    },
    loadRegions: function() {
      var self = this;
      getBillingRegions().then(function(data) {
        self.regions = data.items || [];
      }).catch(function(err) {
        console.error('Failed to load billing regions:', err);
      });
    },
    onRegionChange: function() {
      this.page = 1;
      this.loadData();
    },
    loadData: function() {
      var self = this;
      if (!this.selectedRegion) {
        this.tableState = 'loaded';
        return;
      }

      var token = ++this.loadToken;
      this.error = '';
      this.tableState = 'loading';

      var params = {
        page: this.page,
        pageSize: this.pageSize
      };
      if (this.filters.invoiceType) params.invoiceType = this.filters.invoiceType;

      getBillingInvoices(this.selectedRegion, params).then(function(data) {
        if (token !== self.loadToken) return;
        self.invoices = data.items || [];
        self.totalPages = Math.ceil((data.total || 0) / self.pageSize);
        self.tableState = self.invoices.length === 0 ? 'empty' : 'loaded';
      }).catch(function(err) {
        if (token !== self.loadToken) return;
        console.error('Failed to load invoices:', err);
        self.error = err.message || 'Failed to load invoice data.';
        self.tableState = 'error';
      });
    },
    applyFilters: function() {
      this.page = 1;
      this.loadData();
    },
    goToPage: function(newPage) {
      this.page = newPage;
      this.loadData();
    },
    exportInvoice: function(invoiceId) {
      var self = this;
      if (this.exportingId) return;
      this.exportingId = invoiceId;
      this.exportError = '';
      downloadBillingInvoiceCsv(this.selectedRegion, invoiceId).then(function(response) {
        var blob = new Blob([response.data], { type: 'text/csv' });
        var url = window.URL.createObjectURL(blob);
        var a = document.createElement('a');
        a.href = url;
        a.download = invoiceId + '_' + self.selectedRegion + '.csv';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        self.exportingId = '';
      }).catch(function(err) {
        self.exportingId = '';
        self.exportError = 'Export failed for ' + invoiceId + ': ' + (err.message || 'Unknown error');
      });
    }
  },
  watch: {
    '$route.params.regionCode': function(newRegion) {
      if (newRegion) {
        this.selectedRegion = newRegion.toUpperCase();
        this.page = 1;
        this.loadData();
      }
    }
  },
  mounted: function() {
    this.loadRegions();
    var routeRegion = this.$route.params.regionCode;
    if (routeRegion) {
      this.selectedRegion = routeRegion.toUpperCase();
    }
    if (this.selectedRegion) {
      this.loadData();
    } else {
      this.tableState = 'loaded';
    }
  }
};
</script>

<style scoped>
.billing-invoices {
  min-height: 100%;
}

.page-body {
  padding: 24px;
}

.filters-bar {
  display: flex;
  gap: 16px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 16px;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-label {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #475569;
  white-space: nowrap;
}

.filter-select {
  padding: 6px 12px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  font-size: 0.8125rem;
  color: #334155;
  background: #ffffff;
  font-family: 'Inter', sans-serif;
  cursor: pointer;
}

.table-wrapper {
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  overflow: hidden;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.8125rem;
}

.data-table th {
  text-align: left;
  padding: 10px 12px;
  background: #f8fafc;
  color: #64748b;
  font-weight: 600;
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  border-bottom: 1px solid #e2e8f0;
}

.data-table td {
  padding: 10px 12px;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
}

.data-table tbody tr:hover {
  background: #f8fafc;
}

.invoice-id-cell {
  font-family: monospace;
  font-size: 0.75rem;
  font-weight: 500;
}

.spend-cell {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.spend-value {
  font-size: 0.8125rem;
  font-weight: 600;
  color: #1e293b;
}

.spend-bar-container {
  width: 100%;
  height: 4px;
  background: #f1f5f9;
  border-radius: 2px;
  overflow: hidden;
}

.spend-bar {
  height: 100%;
  background: #4f46e5;
  border-radius: 2px;
  transition: width 0.3s;
}

.type-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: capitalize;
}
.type-reconciliation { background: #eff6ff; color: #2563eb; }
.type-azure-usage { background: #f0fdf4; color: #166534; }

.status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: capitalize;
}
.status-paid { background: #dcfce7; color: #166534; }
.status-due { background: #fef3c7; color: #92400e; }
.status-overdue { background: #fee2e2; color: #991b1b; }
.status-void { background: #f1f5f9; color: #475569; }
.status-unknown { background: #f1f5f9; color: #94a3b8; }

.export-btn {
  background: transparent;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 500;
  color: #475569;
  cursor: pointer;
  transition: all 0.2s;
  font-family: 'Inter', sans-serif;
}
.export-btn:hover { background: #f1f5f9; border-color: #94a3b8; }

.pagination {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: center;
  padding: 16px;
}

.page-btn {
  padding: 6px 14px;
  border: 1px solid #cbd5e1;
  border-radius: 6px;
  background: #ffffff;
  color: #334155;
  font-size: 0.8125rem;
  cursor: pointer;
  font-family: 'Inter', sans-serif;
}
.page-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.page-btn:hover:not(:disabled) { background: #f1f5f9; }

.page-info {
  font-size: 0.8125rem;
  color: #64748b;
}

.empty-wrapper {
  display: flex;
  justify-content: center;
  padding: 48px;
}

.error-message {
  background: #fef2f2;
  border: 1px solid #fecaca;
  color: #991b1b;
  padding: 16px;
  border-radius: 8px;
  font-size: 0.875rem;
}

.loading-state {
  text-align: center;
  padding: 48px;
  color: #94a3b8;
  font-size: 0.875rem;
}
</style>
